import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  X, Loader2, UserPlus, Search, Shield, User as UserIcon, Trash2,
  CheckCircle2, AlertCircle, Power, Eye, EyeOff, Lock, Settings
} from 'lucide-react';
import { API } from '../auth/AuthContext';
import { techsApi } from '../api';

const ROLE_OPTIONS = [['user', 'Operador'], ['tecnico', 'Técnico'], ['vendedor', 'Vendedor'], ['admin', 'Administrador']];
const ROLE_LABEL = { user: 'operador', tecnico: 'técnico', vendedor: 'vendedor' };

function OperatorPermissionsModal({ open, onClose, user, onSaved }) {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    setError('');
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/admin/modules`, { withCredentials: true });
        setCatalog(data);
        setSelected(new Set(user.module_permissions ?? (data.default_by_role?.[user.role] || data.default_operator || [])));
      } catch (err) { setError(err?.response?.data?.detail || 'Erro ao carregar módulos.'); }
      finally { setLoading(false); }
    })();
  }, [open, user]);

  if (!open) return null;

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set((catalog?.modules || []).map(m => m.key)));
  const clearAll = () => setSelected(new Set());
  const applyDefault = () => setSelected(new Set(catalog?.default_by_role?.[user?.role] || catalog?.default_operator || []));

  const save = async () => {
    setSaving(true); setError('');
    try {
      await axios.patch(`${API}/auth/admin/users/${user.user_id}`, { module_permissions: Array.from(selected) }, { withCredentials: true });
      onSaved?.();
      onClose();
    } catch (err) { setError(err?.response?.data?.detail || 'Erro ao salvar permissões.'); }
    finally { setSaving(false); }
  };

  // Agrupa por seção
  const grouped = {};
  (catalog?.modules || []).forEach(m => { (grouped[m.section] ||= []).push(m); });

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4 py-6" data-testid="operator-perms-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <Lock className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h3 className="text-[14px] font-bold text-slate-900 truncate">Privilégios de acesso · {ROLE_LABEL[user?.role] || 'usuário'}</h3>
              <p className="text-[11px] text-slate-500 truncate">{user?.name} · {user?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pt-3 pb-2 border-b border-slate-100 flex flex-wrap items-center gap-2 text-[11px]">
          <span className="text-slate-500">
            <b className="text-emerald-700">{selected.size}</b> de {catalog?.modules?.length || 0} módulos liberados
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <button type="button" onClick={applyDefault} data-testid="perms-apply-default"
              className="px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-800 font-semibold rounded hover:bg-amber-100">
              Padrão do perfil
            </button>
            <button type="button" onClick={selectAll} data-testid="perms-select-all"
              className="px-2.5 py-1 bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold rounded hover:bg-emerald-100">
              Marcar todos
            </button>
            <button type="button" onClick={clearAll} data-testid="perms-clear-all"
              className="px-2.5 py-1 bg-slate-100 border border-slate-200 text-slate-700 font-semibold rounded hover:bg-slate-200">
              Limpar
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {loading && <div className="flex items-center gap-2 text-[12px] text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Carregando catálogo…</div>}
          {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-2 text-[11px]">{error}</div>}
          {Object.entries(grouped).map(([section, items]) => (
            <div key={section}>
              <div className="text-[9px] font-bold text-emerald-700 tracking-wider uppercase mb-1">{section}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {items.map(m => {
                  const on = selected.has(m.key);
                  return (
                    <label key={m.key} data-testid={`perm-${m.key.replace(/\//g, '_') || 'home'}`}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer text-[12px] transition-colors ${on ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                      <input type="checkbox" checked={on} onChange={() => toggle(m.key)} className="w-3.5 h-3.5 accent-emerald-600" />
                      <span className={`truncate ${on ? 'font-semibold text-emerald-900' : 'text-slate-700'}`}>{m.label}</span>
                      {!on && <Lock className="w-3 h-3 text-slate-400 ml-auto flex-shrink-0" />}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
          <div className="text-[10px] text-slate-500 max-w-sm">Módulos marcados ficam liberados no menu lateral; os demais aparecem com cadeado e geram alerta ao clique.</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 bg-white border border-slate-200 text-slate-700 text-[12px] font-semibold rounded-lg">Cancelar</button>
            <button type="button" onClick={save} disabled={saving || loading} data-testid="perms-save"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-[12px] font-semibold rounded-lg">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Salvar permissões
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UsersManagerModal({ open, onClose, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'user' });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [permsUser, setPermsUser] = useState(null);
  const [techs, setTechs] = useState([]);
  const [pendingTech, setPendingTech] = useState(null);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/auth/admin/users`, { withCredentials: true });
      setUsers(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Falha ao carregar usuários.');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) { load(); setCreating(false); techsApi.list().then(setTechs).catch(() => setTechs([])); } }, [open]);
  const linkedTo = (tid) => users.find(u => u.technician_id === tid);

  if (!open) return null;

  const createUser = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/auth/admin/users`, { ...form, technician_id: form.role === 'tecnico' ? form.technician_id : null }, { withCredentials: true });
      setForm({ email: '', name: '', password: '', role: 'user', technician_id: '' });
      setCreating(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Falha ao criar usuário.');
    } finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try {
      await axios.patch(`${API}/auth/admin/users/${u.user_id}`, { active: !u.active }, { withCredentials: true });
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Falha ao atualizar.');
    }
  };

  const changeRole = async (u, role, technician_id) => {
    if (role === 'tecnico' && !technician_id && !u.technician_id) { setPendingTech(u.user_id); return; }
    try {
      await axios.patch(`${API}/auth/admin/users/${u.user_id}`, { role, ...(technician_id ? { technician_id } : {}) }, { withCredentials: true });
      setPendingTech(null);
      await load();
    } catch (err) { setError(err?.response?.data?.detail || 'Falha ao atualizar.'); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Excluir ${u.email}? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/auth/admin/users/${u.user_id}`, { withCredentials: true });
      await load();
    } catch (err) { setError(err?.response?.data?.detail || 'Falha ao excluir.'); }
  };

  const filtered = users.filter(u => {
    const q = query.toLowerCase();
    return !q || u.email.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q);
  });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4 py-6">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-900">Gerenciar usuários</h3>
            <p className="text-[11px] text-slate-500">{users.length} usuários cadastrados</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou e-mail..."
              className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[12px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400"
            />
          </div>
          <button
            onClick={() => setCreating((v) => !v)}
            data-testid="users-new-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold rounded-lg"
          >
            <UserPlus className="w-3.5 h-3.5" /> Novo usuário
          </button>
        </div>

        {creating && (
          <form onSubmit={createUser} className="px-5 py-3 border-b border-slate-100 bg-slate-50 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name-input" placeholder="Nome completo" className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email-input" placeholder="email@empresa.com" className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <input required type={showPw ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password-input" placeholder="Senha inicial (min 8, letra+número)" className="w-full pl-3 pr-9 py-2 bg-white border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30" />
                <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 hover:bg-slate-100">
                  {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-user-role-select" className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-[12px] focus:outline-none focus:ring-2 focus:ring-emerald-500/30">
                {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            {form.role === 'tecnico' && (
              <select required value={form.technician_id || ''} onChange={(e) => setForm({ ...form, technician_id: e.target.value })} data-testid="new-user-technician-select"
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-[12px]">
                <option value="">Vincular ao cadastro do técnico…</option>
                {techs.map(t => <option key={t.id} value={t.id} disabled={!!linkedTo(t.id)}>{t.name}{linkedTo(t.id) ? ` (já vinculado a ${linkedTo(t.id).email})` : ''}</option>)}
              </select>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="px-3 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
              <button type="submit" disabled={busy} data-testid="new-user-submit-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white text-[12px] font-semibold rounded-lg">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Criar
              </button>
            </div>
          </form>
        )}

        {error && (
          <div className="mx-5 mt-3 flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
            <p className="text-[12px] text-rose-700">{error}</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando...</div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-[12px] text-slate-500 py-8">Nenhum usuário encontrado.</p>
          ) : (
            <ul className="space-y-2">
              {filtered.map(u => {
                const isMe = u.user_id === currentUser?.user_id;
                return (
                  <li key={u.user_id} className={`flex items-center gap-3 p-3 rounded-lg border ${u.active ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-70'}`}>
                    <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                      {(u.name || u.email).split(' ').map(s => s[0]).slice(0,2).join('').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-bold text-slate-900 truncate">{u.name}</span>
                        {isMe && <span className="text-[9px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold">você</span>}
                        {u.biometric_enabled && <span title="Biometria ativa" className="text-[9px] px-1.5 py-0.5 bg-slate-900 text-white rounded font-semibold">BIO</span>}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{u.email}{u.technician_id ? ` · técnico: ${techs.find(t => t.id === u.technician_id)?.name || u.technician_id}` : ''}</div>
                      {pendingTech === u.user_id && (
                        <select autoFocus defaultValue="" onChange={(e) => e.target.value && changeRole(u, 'tecnico', e.target.value)} data-testid={`user-link-tech-${u.user_id}`}
                          className="mt-1 text-[11px] border border-amber-300 bg-amber-50 rounded-md px-2 py-1">
                          <option value="">Escolha o técnico vinculado…</option>
                          {techs.map(t => <option key={t.id} value={t.id} disabled={!!linkedTo(t.id)}>{t.name}</option>)}
                        </select>
                      )}
                      {pendingTech === u.user_id && <button type="button" onClick={() => setPendingTech(null)} data-testid={`user-link-tech-cancel-${u.user_id}`} className="ml-2 text-[11px] text-slate-500 underline">cancelar</button>}
                    </div>
                    <select
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value)}
                      disabled={isMe || u.role === 'superadmin'}
                      data-testid={`user-role-select-${u.user_id}`}
                      className="text-[11px] font-semibold border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none disabled:opacity-60"
                    >
                      {u.role === 'superadmin' && <option value="superadmin">Superadmin</option>}
                      {ROLE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    {!['admin', 'superadmin'].includes(u.role) && (
                      <button
                        onClick={() => setPermsUser(u)}
                        data-testid={`user-perms-btn-${u.user_id}`}
                        title="Configurar permissões de módulos"
                        className="p-1.5 rounded-md text-emerald-700 hover:bg-emerald-50 border border-emerald-200">
                        <Settings className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={isMe}
                      title={u.active ? 'Desativar' : 'Ativar'}
                      className={`p-1.5 rounded-md text-[11px] font-semibold border ${u.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200'} disabled:opacity-40`}
                    >
                      <Power className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeUser(u)}
                      disabled={isMe}
                      title="Excluir"
                      className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50 disabled:opacity-40"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      <OperatorPermissionsModal
        open={!!permsUser}
        user={permsUser}
        onClose={() => setPermsUser(null)}
        onSaved={load}
      />
    </div>
  );
}
